<script setup>
import { computed, ref, watch } from 'vue'
import { useAgentAdminStore } from '@/stores/agentAdmin.store'

// RM-208: per-AI-message annotation. Captures WHAT SHOULD HAVE HAPPENED (not
// just replacement wording) plus a root-cause category, and shows any prior
// annotations on the same reply. Creates a durable, PII-redacted feedback
// record server-side.

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: String,
    default: null,
  },
  msgDbIndex: {
    type: Number,
    default: null,
  },
  aiText: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue', 'saved'])

const store = useAgentAdminStore()

const CATEGORY_ITEMS = [
  { title: 'Unknown / not sure', value: 'unknown' },
  { title: 'Prompt instructions', value: 'prompt' },
  { title: 'Intent routing', value: 'routing' },
  { title: 'RM data / retrieval', value: 'retrieval_data' },
  { title: 'Tool behavior', value: 'tool' },
  { title: 'Conversation state', value: 'conversation_state' },
  { title: 'Workflow / product gap', value: 'workflow_product' },
  { title: 'UI', value: 'ui' },
  { title: 'Performance', value: 'performance' },
  { title: 'Code bug', value: 'bug' },
  { title: 'Copy only', value: 'copy_only' },
]

const SEVERITY_ITEMS = [
  { title: 'Low', value: 'low' },
  { title: 'Medium', value: 'medium' },
  { title: 'High', value: 'high' },
]

// The plain-English tracking states shown to admins, mapped onto the model's
// status enum. Kapil marks these on production; a commit is not "Complete".
const STATUS_ITEMS = [
  { title: 'Needs to be fixed', value: 'open' },
  { title: 'In progress', value: 'in_progress' },
  { title: 'Complete', value: 'verified' },
  { title: "Won't fix", value: 'wont_fix' },
]

const statusSaving = ref({}) // feedbackId -> bool

const mode = ref('problem') // 'problem' | 'strong'
const adminNote = ref('')
const expectedBehavior = ref('')
const suggestedReply = ref('')
const category = ref('unknown')
const severity = ref('medium')
const saving = ref(false)
const errorMsg = ref(null)

// Prior annotations already on this reply.
const existing = computed(() =>
  (store.activeFeedback || []).filter(f => f.messageIndex === props.msgDbIndex),
)

const canSave = computed(() => {
  if (mode.value === 'strong') return true
  
  return expectedBehavior.value.trim().length > 0
})

function reset() {
  mode.value = 'problem'
  adminNote.value = ''
  expectedBehavior.value = ''
  suggestedReply.value = ''
  category.value = 'unknown'
  severity.value = 'medium'
  errorMsg.value = null
  saving.value = false
}

watch(() => props.modelValue, val => {
  if (val) reset()
})

function close() {
  emit('update:modelValue', false)
}

async function save() {
  if (!canSave.value || saving.value) return
  saving.value = true
  errorMsg.value = null
  try {
    const payload = mode.value === 'strong'
      ? {
        messageIndex: props.msgDbIndex,
        strongExample: true,
        adminNote: adminNote.value.trim() || undefined,
        category: 'copy_only',
      }
      : {
        messageIndex: props.msgDbIndex,
        expectedBehavior: expectedBehavior.value.trim(),
        adminNote: adminNote.value.trim() || undefined,
        suggestedReply: suggestedReply.value.trim() || undefined,
        category: category.value,
        severity: severity.value,
      }
    await store.saveFeedback(props.conversationId, payload)
    emit('saved')
    close()
  } catch (err) {
    errorMsg.value = err?.response?.data?.error || 'Failed to save annotation'
  } finally {
    saving.value = false
  }
}

function statusColor(status) {
  if (status === 'verified') return 'success'
  if (status === 'wont_fix' || status === 'duplicate') return 'medium-emphasis'
  if (status === 'in_progress' || status === 'ready_for_verification') return 'info'

  return 'warning' // open
}

function categoryLabel(value) {
  return CATEGORY_ITEMS.find(c => c.value === value)?.title || value
}

async function updateStatus(feedback, newStatus) {
  if (!newStatus || newStatus === feedback.status) return
  statusSaving.value = { ...statusSaving.value, [feedback._id]: true }
  try {
    await store.updateFeedback(feedback._id, { status: newStatus })
  } catch (err) {
    errorMsg.value = err?.response?.data?.error || 'Failed to update status'
  } finally {
    statusSaving.value = { ...statusSaving.value, [feedback._id]: false }
  }
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="620"
    scrollable
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between pa-4">
        <span class="text-h6">Annotate AI reply</span>
        <VBtn
          icon="mdi-close"
          size="small"
          variant="text"
          @click="close"
        />
      </VCardTitle>

      <VDivider />

      <VCardText class="pa-4">
        <!-- The reply under review (context) -->
        <div class="annotate__reply mb-4">
          {{ aiText }}
        </div>

        <!-- Prior annotations on this reply -->
        <div
          v-if="existing.length"
          class="mb-4"
        >
          <div class="text-caption text-medium-emphasis mb-2">
            {{ existing.length }} existing annotation{{ existing.length > 1 ? 's' : '' }}
          </div>
          <div
            v-for="f in existing"
            :key="f._id"
            class="annotate__existing mb-2"
          >
            <div class="d-flex align-center ga-2 mb-2">
              <VSelect
                :model-value="f.status"
                :items="STATUS_ITEMS"
                :color="statusColor(f.status)"
                :base-color="statusColor(f.status)"
                :loading="statusSaving[f._id]"
                density="compact"
                variant="outlined"
                hide-details
                style="max-width: 190px"
                class="annotate__status annotate__input"
                @update:model-value="val => updateStatus(f, val)"
              />
              <span
                v-if="f.strongExample"
                class="text-caption text-success"
              >Strong example</span>
              <span
                v-else
                class="text-caption text-medium-emphasis"
              >{{ categoryLabel(f.category) }} · {{ f.severity }}</span>
            </div>
            <div
              v-if="f.expectedBehavior"
              class="text-body-2"
            >
              {{ f.expectedBehavior }}
            </div>
            <a
              v-if="f.resolutionRef"
              :href="f.resolutionRef"
              target="_blank"
              rel="noopener noreferrer"
              class="text-caption"
            >{{ f.resolutionRef }}</a>
          </div>
          <VDivider class="mt-3" />
        </div>

        <!-- Problem vs strong-example -->
        <VBtnToggle
          v-model="mode"
          mandatory
          density="comfortable"
          color="primary"
          class="mb-4"
        >
          <VBtn
            value="problem"
            size="small"
          >
            Flag a problem
          </VBtn>
          <VBtn
            value="strong"
            size="small"
          >
            Strong example
          </VBtn>
        </VBtnToggle>

        <template v-if="mode === 'problem'">
          <VTextarea
            v-model="expectedBehavior"
            label="What should have happened?"
            placeholder="Describe the expected behavior or next step — not just the exact wording."
            rows="3"
            auto-grow
            class="mb-3 annotate__input"
          />
          <VTextarea
            v-model="adminNote"
            label="What was wrong? (optional)"
            rows="2"
            auto-grow
            class="mb-3 annotate__input"
          />
          <VTextarea
            v-model="suggestedReply"
            label="Suggested reply (optional — only if exact wording matters)"
            rows="2"
            auto-grow
            class="mb-3 annotate__input"
          />
          <div class="d-flex ga-3">
            <VSelect
              v-model="category"
              :items="CATEGORY_ITEMS"
              label="Likely cause"
              density="comfortable"
              class="annotate__input"
            />
            <VSelect
              v-model="severity"
              :items="SEVERITY_ITEMS"
              label="Severity"
              density="comfortable"
              style="max-width: 160px"
              class="annotate__input"
            />
          </div>
        </template>

        <template v-else>
          <VTextarea
            v-model="adminNote"
            label="Why is this a strong example? (optional)"
            placeholder="Good tone, correct workflow, handled ambiguity well…"
            rows="3"
            auto-grow
            class="annotate__input"
          />
        </template>

        <div
          v-if="errorMsg"
          class="text-error text-body-2 mt-2"
        >
          {{ errorMsg }}
        </div>
      </VCardText>

      <VDivider />

      <VCardActions class="pa-4">
        <VSpacer />
        <VBtn
          variant="text"
          @click="close"
        >
          Cancel
        </VBtn>
        <VBtn
          color="primary"
          variant="flat"
          :loading="saving"
          :disabled="!canSave"
          @click="save"
        >
          Save annotation
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.annotate__reply {
  padding: 12px 14px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  max-height: 180px;
  overflow-y: auto;
  white-space: pre-wrap;
}

.annotate__existing {
  padding: 10px 12px;
  background: rgba(var(--v-theme-on-surface), 0.03);
  border-radius: 8px;
}

// iOS zoom guard: all inputs must render at >= 16px.
.annotate__input :deep(input),
.annotate__input :deep(textarea),
.annotate__input :deep(.v-field__input) {
  font-size: 16px;
}
</style>
